import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApolloError, useLazyQuery, useMutation } from "@apollo/client";
import { FiDownload, FiImage, FiRefreshCw, FiVideo } from "react-icons/fi";
import { HiArrowUp } from "react-icons/hi2";
import { IoClose } from "react-icons/io5";
import { FaArrowRight } from "react-icons/fa6";
import { observer } from "mobx-react-lite";
import { Tooltip } from "flowbite-react";
import { PiMinus, PiPlus } from "react-icons/pi";
import Clarity from "@microsoft/clarity";
import { floorplannerStore } from "../../store/floorplannerStore";
import { renderStore } from "../../store/renderStore";
import { editorStore } from "../../store/editorStore";
import { userStore } from "../../store/userStore";
import { useExportPreviewSnapshot } from "../../hooks/useExportPreviewSnapshot";
import { useExportPreviewState } from "../../hooks/useExportPreviewState";
import { EXPORT_FORMAT_OPTIONS, ExportFormatId } from "../../utils/exportFormats";
import {
  isContentTooLargeError,
  prepareAiRenderSnapshotFile,
} from "../../utils/prepareAiRenderSnapshot";
import { createFloorplanRenderManifest } from "../../features/floorplan3d/rendering/floorplanRenderManifest";
import { trackFloorplan3DMetric } from "../../features/floorplan3d/metrics/floorplan3DMetrics";
import { capturePosthogEvent } from "../../utils/posthog";
import { buildFloorplanRenderUserItemName } from "../../utils/floorplanRenderUserItemName";
import {
  AiVideoJobDocument,
  AiVideoJobResult,
  FLOORPLAN_AI_RENDER_CREDIT_COST,
  FLOORPLAN_AI_RENDER_MODEL,
  FLOORPLAN_AI_VIDEO_CREDIT_COST,
  GenerateAiVideoDocument,
  LogFloorplanAiRenderErrorDocument,
  RenderFloorplanSceneWithAIDocument,
  getFloorplanAiRenderErrorLogMessage,
  shouldReportFloorplanAiRenderError,
  waitForAiVideoJob,
} from "../../gql/floorplanAIRender";
import { AddUserItemToNewMoodBoardDocument, useUserDocumentsQuery } from "../../gql";
import { DocumentItem } from "./AddToPresentation";

/** Same paper formats as Download project (A4/A3), not Add to project digital formats. */
const RENDER_FORMAT_OPTIONS = EXPORT_FORMAT_OPTIONS.filter(
  (format) => format.supportsScale,
);

type DownloadFileType = "png" | "jpg";

type ModalScreen = "compose" | "success" | "pickProject";

/**
 * Converts a fetched image blob to the selected download file type.
 * PNG is returned as-is; JPG is re-encoded via canvas for a matching extension/MIME.
 */
const blobToDownloadFile = async (
  blob: Blob,
  fileType: DownloadFileType,
  baseName: string,
): Promise<{ objectUrl: string; fileName: string }> => {
  if (fileType === "png") {
    return {
      objectUrl: URL.createObjectURL(blob),
      fileName: `${baseName}.png`,
    };
  }

  const sourceUrl = URL.createObjectURL(blob);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Could not decode render image"));
      img.src = sourceUrl;
    });

    const canvas = document.createElement("canvas");
    canvas.width = image.naturalWidth || image.width;
    canvas.height = image.naturalHeight || image.height;
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Could not prepare JPG download");
    }
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0);

    const jpgBlob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (result) => {
          if (!result) {
            reject(new Error("Could not encode JPG download"));
            return;
          }
          resolve(result);
        },
        "image/jpeg",
        0.92,
      );
    });

    return {
      objectUrl: URL.createObjectURL(jpgBlob),
      fileName: `${baseName}.jpg`,
    };
  } finally {
    URL.revokeObjectURL(sourceUrl);
  }
};

interface Render3DViewModalProps {
  onClose: () => void;
  dropdownRef: React.Ref<HTMLDivElement>;
}

const getErrorCode = (error: unknown) =>
  error instanceof ApolloError
    ? error.graphQLErrors.find((item) => item.extensions?.code)?.extensions?.code
    : undefined;

export const Render3DViewModal: React.FC<Render3DViewModalProps> = observer(
  ({ onClose, dropdownRef }) => {
    const { t } = useTranslation();
    const {
      selectedFormatId,
      selectedFormat,
      setFormatId,
      zoom,
      pan,
      previewSize,
      drawingPreviewSize,
      drawablePixelSize,
      handleWheelZoom,
      handleZoomStep,
      handlePan,
    } = useExportPreviewState({
      // Landscape keeps the preview closer to square than portrait paper sizes.
      defaultFormatId: "a4-landscape",
      maxPreviewDimension: 360,
    });

    const [screen, setScreen] = useState<ModalScreen>("compose");
    const [isInitializing, setIsInitializing] = useState(true);
    const [isGenerating, setIsGenerating] = useState(false);
    const [prompt, setPrompt] = useState("");
    const [userItemName, setUserItemName] = useState(() =>
      buildFloorplanRenderUserItemName(floorplannerStore.name),
    );
    const [downloadFileType, setDownloadFileType] = useState<DownloadFileType>("png");
    const [renderResultUrl, setRenderResultUrl] = useState<string | null>(null);
    const [renderUserItemId, setRenderUserItemId] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [serverOutOfCredits, setServerOutOfCredits] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState<any | null>(null);
    const [redirecting, setRedirecting] = useState(false);
    const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
    const [generatedVideoUrl, setGeneratedVideoUrl] = useState<string | null>(
      null,
    );
    const [videoSuccessMessage, setVideoSuccessMessage] = useState<string | null>(
      null,
    );

    const imgRef = useRef<HTMLDivElement>(null);
    const promptRef = useRef<HTMLTextAreaElement>(null);

    const { data, error: documentsError } = useUserDocumentsQuery({
      fetchPolicy: "cache-and-network",
    });

    const [renderScene] = useMutation(RenderFloorplanSceneWithAIDocument);
    const [logAiRenderError] = useMutation(LogFloorplanAiRenderErrorDocument);
    const [generateAiVideo] = useMutation(GenerateAiVideoDocument);
    const [fetchAiVideoJob] = useLazyQuery(AiVideoJobDocument, {
      fetchPolicy: "network-only",
    });

    useExportPreviewSnapshot({
      selectedFormatId,
      selectedFormat,
      drawablePixelSize,
      setIsInitializing,
    });

    useEffect(() => {
      if (documentsError) {
        console.error("Error fetching user documents:", documentsError);
      }
    }, [documentsError]);

    const plan = userStore.entitlements?.plan?.toUpperCase() ?? "FREE";
    const credits = userStore.entitlements?.aiCredits;
    const canBuyCredits = plan !== "ENTERPRISE";
    const outOfCredits =
      serverOutOfCredits ||
      Boolean(
        credits &&
          !credits.unlimited &&
          (credits.remaining ?? 0) < FLOORPLAN_AI_RENDER_CREDIT_COST,
      );
    const outOfVideoCredits =
      serverOutOfCredits ||
      Boolean(
        credits &&
          !credits.unlimited &&
          (credits.remaining ?? 0) < FLOORPLAN_AI_VIDEO_CREDIT_COST,
      );

    const buyCredits = () => {
      const clientUrl = process.env.REACT_APP_CLIENT_URI;
      if (!clientUrl) return;
      window.parent.location.href = `${clientUrl}/mypage/account?buyCredits=1`;
    };

    const resetSuccessState = () => {
      setRenderResultUrl(null);
      setRenderUserItemId(null);
      setErrorMessage(null);
      setServerOutOfCredits(false);
      setIsGeneratingVideo(false);
      setGeneratedVideoUrl(null);
      setVideoSuccessMessage(null);
    };

    const handleGenerate = async () => {
      if (outOfCredits || isGenerating || !editorStore.camera) return;

      setErrorMessage(null);
      setIsGenerating(true);
      editorStore.setRender3DViewGenerating(true);

      let snapshotBytes: number | undefined;
      let manifestBytes: number | undefined;
      try {
        await new Promise((resolve) => setTimeout(resolve, 100));
        const target = {
          orientation: selectedFormat.orientation,
          format: selectedFormat.format,
          width: selectedFormat.width,
          height: selectedFormat.height,
        };

        await renderStore.capture3DSnapshot("aiRender", target, editorStore.camera, undefined, {
          pan,
          zoom,
          previewSize,
        });

        if (!renderStore.imageData) {
          throw new Error("Could not capture the 3D view. Try adjusting the preview area.");
        }

        const snapshotFile = await prepareAiRenderSnapshotFile(
          renderStore.imageData,
          `floorplan_render_${Date.now()}.jpg`,
        );
        snapshotBytes = snapshotFile?.size;

        const manifest = createFloorplanRenderManifest({
          store: floorplannerStore,
          camera: editorStore.camera,
          viewport: {
            width: selectedFormat.width,
            height: selectedFormat.height,
            dpr: window.devicePixelRatio,
          },
        });
        manifestBytes = new Blob([JSON.stringify(manifest)]).size;

        trackFloorplan3DMetric("ai_render_manifest_created", {
          format: selectedFormatId,
          snapshotBytes,
          manifestBytes,
        });

        const { data: mutationData } = await renderScene({
          variables: {
            manifest,
            snapshot: snapshotFile,
            prompt: prompt.trim() || undefined,
            model: FLOORPLAN_AI_RENDER_MODEL,
            name:
              userItemName.trim() ||
              buildFloorplanRenderUserItemName(floorplannerStore.name),
          },
        });

        const result = mutationData?.renderFloorplanSceneWithAI;
        if (!result?.imageUrl || !result?.userItemId) {
          throw new Error("The render completed but no image was returned.");
        }

        setRenderResultUrl(result.imageUrl);
        setRenderUserItemId(result.userItemId);
        setScreen("success");
        trackFloorplan3DMetric("ai_render_completed", {
          model: result.model,
          format: selectedFormatId,
        });
        await userStore.fetchCurrentUser();
      } catch (generateError) {
        if (getErrorCode(generateError) === "AI_CREDIT_LIMIT_REACHED") {
          setServerOutOfCredits(true);
        }
        if (isContentTooLargeError(generateError)) {
          trackFloorplan3DMetric("ai_render_payload_too_large", {
            format: selectedFormatId,
          });
        }
        const message = isContentTooLargeError(generateError)
          ? t("render3d.contentTooLarge")
          : generateError instanceof ApolloError
            ? (generateError.graphQLErrors[0]?.message ?? generateError.message)
            : generateError instanceof Error
              ? generateError.message
              : "Something went wrong. Please try again.";
        setErrorMessage(message);
        if (shouldReportFloorplanAiRenderError(generateError)) {
          try {
            await logAiRenderError({
              variables: {
                input: {
                  errorMessage: [
                    getFloorplanAiRenderErrorLogMessage(generateError),
                    manifestBytes != null ? `manifestBytes=${manifestBytes}` : null,
                  ]
                    .filter(Boolean)
                    .join(" | "),
                  format: selectedFormatId,
                  snapshotBytes,
                  prompt: prompt.trim() || undefined,
                  model: FLOORPLAN_AI_RENDER_MODEL,
                },
              },
            });
          } catch (logError) {
            console.error("Failed to log floorplan AI render error", logError);
          }
        }
      } finally {
        setIsGenerating(false);
        editorStore.setRender3DViewGenerating(false);
      }
    };

    const handleRedo = () => {
      if (isGeneratingVideo) return;
      resetSuccessState();
      setScreen("compose");
    };

    /**
     * Animate the completed AI render into a short MP4 (Uploads → Videos in BONI).
     * Takes one to a few minutes; keep the modal open until it finishes.
     */
    const handleGenerateVideo = async () => {
      if (
        !renderResultUrl ||
        isGeneratingVideo ||
        outOfVideoCredits ||
        generatedVideoUrl
      ) {
        return;
      }

      setErrorMessage(null);
      setVideoSuccessMessage(null);
      setIsGeneratingVideo(true);
      editorStore.setRender3DViewGenerating(true);

      try {
        const { data: videoData } = await generateAiVideo({
          variables: {
            imageUrl: renderResultUrl,
            prompt: null,
          },
        });
        let result = videoData?.generateAiVideo as AiVideoJobResult | undefined;
        if (!result?.jobId) {
          throw new Error("Video generation returned no job");
        }
        if (!(result.status === "COMPLETED" && result.videoUrl)) {
          result = await waitForAiVideoJob(async () => {
            const { data: pollData } = await fetchAiVideoJob({
              variables: { id: result!.jobId },
            });
            return pollData?.aiVideoJob as AiVideoJobResult | undefined;
          });
        }
        if (!result?.videoUrl) {
          throw new Error("Video generation returned no URL");
        }

        setGeneratedVideoUrl(result.videoUrl);
        setVideoSuccessMessage(
          t("render3d.videoSuccess", { credits: result.creditsConsumed }),
        );
        await userStore.fetchCurrentUser();
        trackFloorplan3DMetric("ai_video_completed", {
          creditsConsumed: result.creditsConsumed,
        });
        capturePosthogEvent("floorplan_ai_video_completed", {
          creditsConsumed: result.creditsConsumed,
        });
      } catch (videoError) {
        const code = getErrorCode(videoError);
        if (
          code === "AI_CREDIT_LIMIT_REACHED" ||
          code === "AI_IMAGE_LIMIT_REACHED"
        ) {
          setServerOutOfCredits(true);
        }
        const message =
          videoError instanceof ApolloError
            ? (videoError.graphQLErrors[0]?.message ?? videoError.message)
            : videoError instanceof Error
              ? videoError.message
              : t("render3d.videoFailed");
        setErrorMessage(message);
      } finally {
        setIsGeneratingVideo(false);
        editorStore.setRender3DViewGenerating(false);
      }
    };

    const uploadsVideosUrl = (() => {
      const clientUrl = process.env.REACT_APP_CLIENT_URI;
      if (!clientUrl) return null;
      return `${clientUrl.replace(/\/$/, "")}/mypage/user-items?filter=video`;
    })();

    const handleDownload = async () => {
      if (!renderResultUrl) return;
      try {
        const response = await fetch(renderResultUrl);
        const blob = await response.blob();
        const baseName = `${floorplannerStore.name || "floorplan"}_render`;
        const { objectUrl, fileName } = await blobToDownloadFile(
          blob,
          downloadFileType,
          baseName,
        );
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(objectUrl);
      } catch (downloadError) {
        console.error("Failed to download render:", downloadError);
        setErrorMessage("Could not download the render. Please try again.");
      }
    };

    const handleAttachToProject = async () => {
      if (!renderUserItemId || !selectedDocument) return;

      setRedirecting(true);
      editorStore.setRender3DViewGenerating(true);
      setErrorMessage(null);

      try {
        const addToBoardResult = await renderStore.client?.mutate({
          mutation: AddUserItemToNewMoodBoardDocument,
          variables: {
            documentId: selectedDocument.id,
            userItemId: renderUserItemId,
          },
        });

        const newMoodBoard = addToBoardResult?.data?.addUserItemToNewMoodBoard;
        if (!newMoodBoard) {
          throw new Error("Failed to add render to project");
        }

        Clarity.event("add_to_presentation");
        capturePosthogEvent("add_to_presentation");
        window.location.href = `${process.env.REACT_APP_CLIENT_URI}/editor/${selectedDocument.id}#scroll-last-page`;
      } catch (attachError) {
        console.error("Error attaching render to project:", attachError);
        const message =
          attachError instanceof ApolloError
            ? (attachError.graphQLErrors[0]?.message ?? attachError.message)
            : attachError instanceof Error
              ? attachError.message
              : "Something went wrong. Please try again.";
        setErrorMessage(message);
        setRedirecting(false);
        editorStore.setRender3DViewGenerating(false);
      }
    };

    const previewImageSrc =
      screen === "success" && renderResultUrl ? renderResultUrl : renderStore.imageData;

    /** Blocks closing and background UI while a render, video, or redirect is in flight. */
    const isUiLocked = isGenerating || isGeneratingVideo || redirecting;

    const handleRequestClose = () => {
      if (isUiLocked) return;
      onClose();
    };

    return (
      <div
        ref={dropdownRef}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-[#13030366]"
        role="presentation"
        onMouseDown={(event) => {
          // Dismiss only via backdrop clicks; ignore while generation locks the UI.
          if (event.target === event.currentTarget && !isUiLocked) {
            handleRequestClose();
          }
        }}
      >
        {/**
         * Full-page white overlay while generating.
         * Text sits above a ring spinner with a static image icon in the center.
         */}
        {isGenerating && (
          <div
            className="absolute inset-0 z-[110] flex flex-col items-center justify-center bg-white/80"
            role="status"
            aria-live="polite"
          >
            <div className="mb-5 text-2xl font-bold text-gray-900">
              {t("render3d.generating")}
            </div>
            <div className="relative flex h-14 w-14 items-center justify-center">
              <div
                className="absolute inset-0 animate-spin rounded-full border-2 border-gray-300 border-t-gray-800"
                aria-hidden="true"
              />
              <FiImage className="h-6 w-6 text-gray-700" aria-hidden="true" />
            </div>
          </div>
        )}
        <div
          className="relative z-10 w-[600px] max-h-[min(920px,calc(100vh-2rem))] overflow-y-auto rounded border border-gray-300 bg-[#EDEBE6] shadow-md"
          role="dialog"
          aria-modal="true"
          aria-busy={isUiLocked}
          aria-labelledby="render-3d-view-title"
          onWheel={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
        >
        <div className="flex flex-cols items-center justify-between p-2">
          <div id="render-3d-view-title" className="text-sm font-normal">
            {t("render3d.render3dView")}
          </div>
          <button
            type="button"
            onClick={handleRequestClose}
            disabled={isUiLocked}
            className="cursor-pointer text-sm text-gray-500 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label={t("render3d.closeModal")}
          >
            <IoClose style={{ strokeWidth: 0.5 }} size={24} />
          </button>
        </div>
        <div className="w-full border-b border-gray-300" />

        {screen === "compose" && (
          <>
            <div className="mt-2 flex w-full flex-col items-center justify-between px-10">
              <div className="w-full text-md font-normal">{t("common.selectArea")}</div>
              <div className="w-full text-sm text-gray-500">
                {t("common.zoomAreaRenderHint")}
              </div>

              {isInitializing ? (
                <div
                  className="flex w-full items-center justify-center p-8"
                  style={{ height: `${previewSize.height}px` }}
                >
                  <div className="mr-3 h-8 w-8 animate-spin rounded-full border-b-2 border-gray-600" />
                  <span className="text-gray-600">{t("common.initializingPreview")}</span>
                </div>
              ) : (
                /**
                 * Center the crop frame in the modal. Zoom controls sit outside the
                 * overflow-hidden preview so they stay visible without shifting layout.
                 */
                <div className="relative mt-2 flex w-full items-center justify-center">
                  <div className="relative mt-4">
                    <div className="overflow-hidden rounded border border-gray-300 bg-white shadow-md">
                      <div
                        className="relative overflow-hidden bg-white"
                        style={{
                          cursor: isGenerating ? "default" : "grab",
                          width: `${previewSize.width}px`,
                          height: `${previewSize.height}px`,
                        }}
                        onWheel={isGenerating ? undefined : handleWheelZoom}
                        onMouseMove={isGenerating ? undefined : handlePan}
                      >
                        <div
                          className="absolute"
                          ref={imgRef}
                          style={{
                            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                            transformOrigin: "center center",
                            width: drawingPreviewSize.width,
                            height: previewSize.height,
                          }}
                        >
                          {previewImageSrc ? (
                            <img
                              src={previewImageSrc}
                              alt={floorplannerStore.name}
                              className="h-full w-full object-contain"
                              draggable={false}
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center text-xs text-gray-500">
                              {t("render3d.preparingPreview")}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="absolute left-full top-1/2 ml-2 flex -translate-y-1/2 flex-col space-y-2">
                      <Tooltip content={t("common.zoomIn")} placement="right">
                        <button
                          type="button"
                          onClick={() => handleZoomStep(0.1)}
                          disabled={isGenerating}
                          className="rounded-sm border border-gray-300 bg-white p-2 text-center shadow-md hover:bg-gray-100 disabled:opacity-50"
                        >
                          <PiPlus size={20} />
                        </button>
                      </Tooltip>
                      <Tooltip content={t("common.zoomOut")} placement="right">
                        <button
                          type="button"
                          onClick={() => handleZoomStep(-0.1)}
                          disabled={isGenerating}
                          className="rounded-sm border border-gray-300 bg-white p-2 text-center shadow-md hover:bg-gray-100 disabled:opacity-50"
                        >
                          <PiMinus size={20} />
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex h-full w-full flex-col items-center justify-between px-10">
              <div className="flex w-full flex-cols items-start justify-between gap-4 p-2">
                <div className="w-60 text-sm text-gray-500">
                  <label htmlFor="render-file-type">{t("render3d.fileType")}</label>
                  <br />
                  <select
                    id="render-file-type"
                    name="render-file-type"
                    className="mt-2 w-full"
                    value={downloadFileType}
                    disabled={isGenerating}
                    onChange={(event) =>
                      setDownloadFileType(event.target.value as DownloadFileType)
                    }
                  >
                    <option value="png">{t("common.png")}</option>
                    <option value="jpg">{t("common.jpg")}</option>
                  </select>
                </div>
                <div className="w-60 text-sm text-gray-500">
                  <label htmlFor="render-format">{t("common.format")}</label>
                  <br />
                  <select
                    id="render-format"
                    name="render-format"
                    className="mt-2 w-full"
                    value={selectedFormatId}
                    disabled={isGenerating}
                    onChange={(event) => setFormatId(event.target.value as ExportFormatId)}
                  >
                    {RENDER_FORMAT_OPTIONS.map((format) => (
                      <option key={format.id} value={format.id}>
                        {format.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="w-full p-2 text-sm text-gray-500">
                <label htmlFor="render-item-name">{t("render3d.nameLabel")}</label>
                <input
                  id="render-item-name"
                  type="text"
                  value={userItemName}
                  disabled={isGenerating}
                  onChange={(event) => setUserItemName(event.target.value)}
                  className="mt-2 h-10 w-full rounded border border-gray-300 bg-white px-2 py-2 text-sm leading-none text-gray-800 outline-none focus:border-gray-500 disabled:opacity-60"
                />
              </div>

              <div className="w-full p-2 text-sm text-gray-500">
                <label htmlFor="render-prompt">{t("render3d.renderPrompt")}</label>
                <div className="mt-2 flex items-center gap-2">
                  <textarea
                    id="render-prompt"
                    ref={promptRef}
                    rows={1}
                    value={prompt}
                    disabled={isGenerating}
                    onChange={(event) => setPrompt(event.target.value)}
                    placeholder={t("render3d.describePrompt")}
                    className="h-10 w-full resize-none rounded border border-gray-300 bg-white px-2 py-2 text-sm leading-none text-gray-800 outline-none focus:border-gray-500 disabled:opacity-60"
                  />
                  <button
                    type="button"
                    aria-label={t("render3d.submitPrompt")}
                    disabled={isGenerating || outOfCredits}
                    onClick={() => void handleGenerate()}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-black text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <HiArrowUp size={18} />
                  </button>
                </div>
              </div>

              {outOfCredits && (
                <div
                  role="status"
                  className="mb-2 w-full rounded-xl bg-[#fff2c2] px-4 py-3 text-sm text-[#342b0a]"
                >
                  <div className="font-semibold">{t("common.notEnoughAiCredits")}</div>
                  <p className="mt-1 leading-5">
                    {canBuyCredits
                      ? t("render3d.buyCreditsHint")
                      : t("render3d.contactAdminHint")}
                  </p>
                </div>
              )}

              {outOfCredits ? (
                canBuyCredits && (
                  <button
                    type="button"
                    onClick={buyCredits}
                    className="mb-4 mt-2 w-full rounded-md bg-boniYellowPrimary p-4 text-sm font-semibold text-black"
                  >
                    {t("common.buyMoreAiCredits")}
                  </button>
                )
              ) : (
                <button
                  type="button"
                  disabled={isGenerating || isInitializing}
                  onClick={() => void handleGenerate()}
                  className={
                    isGenerating || isInitializing
                      ? "mb-4 mt-2 w-full cursor-not-allowed rounded-md bg-gray-600 p-4 text-white"
                      : "mb-4 mt-2 w-full rounded-md bg-black p-4 text-white"
                  }
                >
                  <div className="flex flex-row items-center justify-center">
                    {t("render3d.renderThisView")}
                    &nbsp; <FaArrowRight className="transform scale-90 text-xl" />
                  </div>
                </button>
              )}
            </div>
          </>
        )}

        {screen === "success" && (
          <div className="flex w-full flex-col items-center px-10 pb-6 pt-4">
            <div className="w-full text-md font-normal">
              {generatedVideoUrl
                ? t("render3d.videoCompleted")
                : t("render3d.completed")}
            </div>
            <div className="relative mt-4 w-full overflow-hidden rounded border border-gray-300 bg-white shadow-md">
              {generatedVideoUrl ? (
                <video
                  key={generatedVideoUrl}
                  src={generatedVideoUrl}
                  poster={renderResultUrl || undefined}
                  controls
                  autoPlay
                  playsInline
                  className="h-auto w-full bg-black object-contain"
                />
              ) : (
                renderResultUrl && (
                  <img
                    src={renderResultUrl}
                    alt="AI rendered floorplan view"
                    className="h-auto w-full object-contain"
                  />
                )
              )}
              {isGeneratingVideo && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-white/80 px-4 text-center backdrop-blur-[1px]">
                  <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700" />
                  <p className="text-sm font-medium text-gray-800">
                    {t("render3d.generatingVideo")}
                  </p>
                  <p className="text-xs text-gray-500">
                    {t("render3d.generatingVideoHint")}
                  </p>
                </div>
              )}
            </div>
            <div className="mt-4 flex w-full flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={handleRedo}
                disabled={isGeneratingVideo}
                className={
                  isGeneratingVideo
                    ? "flex flex-1 cursor-not-allowed items-center justify-center gap-2 rounded-md border border-gray-300 bg-white p-3 text-sm font-semibold text-gray-400"
                    : "flex flex-1 items-center justify-center gap-2 rounded-md border border-black bg-white p-3 text-sm font-semibold text-black"
                }
              >
                <FiRefreshCw />
                {t("render3d.redoRender")}
              </button>
              <button
                type="button"
                onClick={() => void handleDownload()}
                disabled={isGeneratingVideo}
                className={
                  isGeneratingVideo
                    ? "flex flex-1 cursor-not-allowed items-center justify-center gap-2 rounded-md bg-gray-400 p-3 text-sm font-semibold text-white"
                    : "flex flex-1 items-center justify-center gap-2 rounded-md bg-black p-3 text-sm font-semibold text-white"
                }
              >
                <FiDownload />
                {t("render3d.downloadFormat", { format: downloadFileType.toUpperCase() })}
              </button>
              <button
                type="button"
                onClick={() => setScreen("pickProject")}
                disabled={isGeneratingVideo}
                className={
                  isGeneratingVideo
                    ? "flex flex-1 cursor-not-allowed items-center justify-center gap-2 rounded-md bg-gray-200 p-3 text-sm font-semibold text-gray-400"
                    : "flex flex-1 items-center justify-center gap-2 rounded-md bg-boniYellowPrimary p-3 text-sm font-semibold text-black"
                }
              >
                {t("common.addToProject")}
                <FaArrowRight className="transform scale-90 text-xl" />
              </button>
            </div>
            {!generatedVideoUrl && (
              <button
                type="button"
                onClick={() => void handleGenerateVideo()}
                disabled={
                  isGeneratingVideo || outOfVideoCredits || !renderResultUrl
                }
                className={
                  isGeneratingVideo || outOfVideoCredits
                    ? "mt-2 flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-md border border-gray-300 bg-gray-100 p-3 text-sm font-semibold text-gray-400"
                    : "mt-2 flex w-full items-center justify-center gap-2 rounded-md border border-black bg-white p-3 text-sm font-semibold text-black hover:bg-gray-50"
                }
              >
                {isGeneratingVideo ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700" />
                ) : (
                  <FiVideo />
                )}
                {isGeneratingVideo
                  ? t("render3d.generatingVideo")
                  : t("render3d.generateVideo")}
              </button>
            )}
            {generatedVideoUrl && uploadsVideosUrl ? (
              <a
                href={uploadsVideosUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-md border border-black bg-white p-3 text-sm font-semibold text-black hover:bg-gray-50"
              >
                <FiVideo />
                {t("render3d.openVideosInUploads")}
                <FaArrowRight className="transform scale-90 text-xl" />
              </a>
            ) : null}
            <p className="mt-1 w-full text-center text-xs text-gray-500">
              {videoSuccessMessage
                ? videoSuccessMessage
                : outOfVideoCredits
                  ? canBuyCredits
                    ? t("render3d.videoBuyCreditsHint")
                    : t("render3d.contactAdminHint")
                  : t("render3d.generateVideoHint", {
                      credits: FLOORPLAN_AI_VIDEO_CREDIT_COST,
                    })}
            </p>
            {outOfVideoCredits && canBuyCredits && !generatedVideoUrl && (
              <button
                type="button"
                onClick={buyCredits}
                className="mt-1 text-xs font-semibold text-black underline"
              >
                {t("common.buyMoreAiCredits")}
              </button>
            )}
          </div>
        )}

        {screen === "pickProject" && (
          <div className="mt-2 flex w-full flex-col items-center justify-between px-10 pb-6">
            <div className="w-full text-md font-normal">{t("common.chooseProject")}</div>
            <div className="w-full text-sm text-gray-500">
              {t("addToPresentation.clickRenderProject")}
            </div>

            <div className="mt-2 grid h-[350px] w-full grid-cols-3 gap-2 overflow-scroll">
              {data?.userDocuments?.documents?.map((document: any) => (
                <DocumentItem
                  key={document.id}
                  document={document}
                  selectDocument={(nextDocument) => setSelectedDocument(nextDocument)}
                  selected={selectedDocument?.id === document.id}
                />
              ))}
            </div>
            <button
              type="button"
              className={
                selectedDocument === null || redirecting
                  ? "mb-4 mt-4 w-full cursor-not-allowed rounded-md bg-gray-600 p-4 text-white"
                  : "mb-4 mt-4 w-full rounded-md bg-black p-4 text-white"
              }
              disabled={selectedDocument === null || redirecting}
              onClick={() => void handleAttachToProject()}
            >
              <div className="flex flex-row items-center justify-center">
                {redirecting && (
                  <div className="mr-3 h-5 w-5 animate-spin rounded-full border-b-2 border-white" />
                )}
                {!redirecting && <>{t("common.addToProject")}</>}
                &nbsp; <FaArrowRight className="transform scale-90 text-xl" />
              </div>
            </button>
          </div>
        )}

        {errorMessage && (
          <div className="mx-10 mb-4 flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
            <span className="mt-0.5 shrink-0">⚠</span>
            <span>{errorMessage}</span>
          </div>
        )}
        </div>
      </div>
    );
  },
);

export default Render3DViewModal;
